import { SelectProps } from "@radix-ui/react-select";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/molecules/Form";
import { cn } from "@/lib/utils";
import { Select, SelectTrigger, SelectValue } from "./Select";

export type SelectFieldProps = SelectProps &
  Omit<React.ComponentProps<"select">, "prefix"> & {
    name: string;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    testId?: string;
    triggerClassName?: string;
    label?: React.ReactNode;
    subcopy?: React.ReactNode;
    note?: React.ReactNode;
    fullWidth?: boolean;
    readOnly?: boolean;
    placeholder?: React.ReactNode;
    labelDataTestingId?: string;
    buttonDataTestingId?: string;
    renderContent: (props: any) => React.ReactNode;
    getDynamicLabel?: (fieldName: string) => React.ReactNode;
  };

export const SelectField = ({
  prefix,
  suffix,
  testId = "select",
  className,
  triggerClassName,
  label,
  subcopy,
  note,
  name,
  fullWidth = false,
  renderContent,
  readOnly,
  placeholder,
  labelDataTestingId,
  buttonDataTestingId,
  getDynamicLabel,
  ...selectProps
}: SelectFieldProps) => {
  const { control } = useFormContext();
  const finalLabel = getDynamicLabel ? getDynamicLabel(name) : label;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, ...rest }) => {
        return (
          <FormItem
            disabled={selectProps.disabled}
            readOnly={readOnly}
            className={cn("tw-flex-1", { "tw-w-full": fullWidth }, className)}
          >
            {!!finalLabel && (
              <FormLabel
                subcopy={subcopy}
                required={selectProps.required}
                data-testing-id={labelDataTestingId}
              >
                {finalLabel}
              </FormLabel>
            )}

            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              value={field.value}
              {...selectProps}
            >
              <FormControl forwardErrorMsg>
                <SelectTrigger
                  data-testing-id={buttonDataTestingId}
                  fullWidth={fullWidth}
                  className={triggerClassName}
                  prefix={prefix}
                  suffix={suffix}
                  ref={field.ref}
                  testId={testId ? `${testId}-trigger` : undefined}
                >
                  <SelectValue
                    disabled={selectProps.disabled}
                    placeholder={placeholder}
                  />
                </SelectTrigger>
              </FormControl>

              {renderContent({
                field,
                ...rest,
              })}
            </Select>

            <FormMessage>{note}</FormMessage>
          </FormItem>
        );
      }}
    />
  );
};

SelectField.displayName = "SelectField";
